import { Link, useParams } from 'react-router';
import { CHARACTER_REGIONS, type CharacterRegion, pageTitle } from '@thottbot/shared';
import { useCharacter } from '../api/client';
import { CharacterSheet } from '../features/characters/CharacterSheet';
import { GearTable } from '../features/characters/GearTable';
import { TalentTrees } from '../features/characters/TalentTrees';
import { NotFound } from './NotFound';
import { relativeTime } from '../util/time';

function isRegion(r: string | undefined): r is CharacterRegion {
  return !!r && (CHARACTER_REGIONS as readonly string[]).includes(r);
}

export function CharacterPage() {
  const { region, realm, name } = useParams();
  const valid = isRegion(region) && !!realm && !!name;
  const result = useCharacter(valid ? region : 'us', realm ?? '', name ?? '');
  if (!valid) return <NotFound message="No such character." />;
  if (result.isPending) return <div className="page muted">Loading...</div>;
  if (result.isError) {
    if (result.error.status === 404) return <NotFound message="No such character." />;
    return <div className="page error">Could not load this character.</div>;
  }
  const c = result.data;
  return (
    <div className="page">
      <title>{pageTitle(c.name, c.realmName)}</title>
      <h1>
        {c.name}
        {!c.public && (
          <span className="badge private" title="Only you can see this page">
            Private
          </span>
        )}
      </h1>
      <p className="muted" data-testid="character-imported">
        Imported {relativeTime(c.importedAt)} from {c.label} by {c.ownerBattletag}
      </p>
      {c.detailStatus === 'pending' && (
        <p className="notice" data-testid="character-pending">
          Gear and talents are still loading from Battle.net.
        </p>
      )}
      {c.detailStatus === 'failed' && (
        <p className="notice error" data-testid="character-failed">
          Gear and talents could not be read{c.error ? `: ${c.error}` : ''}.
        </p>
      )}
      <p className="actions">
        <Link to={`/build?character=${c.id}`} data-testid="character-build">
          Build for this character
        </Link>
      </p>
      <CharacterSheet c={c} />
      <p className="actions" data-testid="character-summary">
        <span>
          Level {c.level} {c.race} {c.class}
        </span>
        <span>·</span>
        <span>
          {c.realmName} ({CHARACTER_REGIONS.includes(c.region) ? c.region.toUpperCase() : c.region})
        </span>
        <span>·</span>
        <span>{c.faction}</span>
        {c.guild && (
          <>
            <span>·</span>
            <span>{c.guild}</span>
          </>
        )}
      </p>
      <section className="section">
        <h2>Gear</h2>
        <GearTable gear={c.gear} />
      </section>
      <section className="section">
        <h2>Talents{c.talents.specialization ? ` (${c.talents.specialization})` : ''}</h2>
        <TalentTrees talents={c.talents} />
      </section>
    </div>
  );
}
